import React, { useState, useEffect, useCallback } from "react";
import { Button, Typography, Table, Sheet, CircularProgress, Box, IconButton, Tooltip } from "@mui/joy";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";

import { useToast } from "../../context/ToastContext";
import { useModal } from "../../context/ModalContext";

import { PostgresChangePayload, Student } from "../../types";
import { ParentGuardian, RelationshipType, RelationshipTypeLabels } from "../../types/ParentGuardian";

import { supabase } from "../../supabaseClient";

import AddParentGuardianToStudentModal from "../modals/AddParentGuardianToStudentModal";

interface StudentParentGuardiansTableProps {
  student: Student;
}

const StudentParentGuardiansTable: React.FC<StudentParentGuardiansTableProps> = ({ student }) => {
  const [parentGuardians, setParentGuardians] = useState<ParentGuardian[]>([]);
  const [loading, setLoading] = useState(true);
  const { showToast } = useToast();
  const { openModal } = useModal();

  const fetchParentGuardians = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("students_parent_guardians")
        .select(`
          parent_guardians (
            id,
            first_name,
            last_name,
            phone_number,
            email_address
          ),
          relationship
        `)
        .eq("student_id", student.id);

      if (error) {
        throw error;
      }

      const formattedParentGuardians: ParentGuardian[] = (data || []).map((entry: any) => {
        const parentGuardian = entry.parent_guardians;
        if (!parentGuardian || typeof parentGuardian !== "object") {
          console.error("Invalid parent/guardian data:", entry);
          return null;
        }

        return {
          id: parentGuardian.id,
          first_name: parentGuardian.first_name,
          last_name: parentGuardian.last_name,
          phone_number: parentGuardian.phone_number,
          email_address: parentGuardian.email_address,
          relationship: entry.relationship,
        } as ParentGuardian;
      }).filter((pg): pg is ParentGuardian => pg !== null);

      setParentGuardians(formattedParentGuardians);
    } catch (error) {
      console.error("Error fetching parent/guardian data:", error);
      showToast("Error fetching parent/guardian data.", "danger", "solid");
    } finally {
      setLoading(false);
    }
  }, [student.id, showToast]);

  const fetchParentGuardianWithRelations = useCallback(async (parentGuardianId: string) => {
    try {
      const { data, error } = await supabase
        .from("students_parent_guardians")
        .select(`
          parent_guardians (
            id,
            first_name,
            last_name,
            phone_number,
            email_address
          ),
          relationship
        `)
        .eq("parent_guardian_id", parentGuardianId)
        .eq("student_id", student.id)
        .single();

      if (error) {
        console.error("Error fetching parent/guardian with relations:", error);
        throw error;
      }

      if (!data || !data.parent_guardians) {
        console.warn("Parent/guardian data is missing or invalid:", data);
        return null;
      }

      const parentGuardian: any = data.parent_guardians;

      return {
        id: parentGuardian.id,
        first_name: parentGuardian.first_name,
        last_name: parentGuardian.last_name,
        phone_number: parentGuardian.phone_number,
        email_address: parentGuardian.email_address,
        relationship: data.relationship,
      } as ParentGuardian;
    } catch (error) {
      console.error("Error fetching parent/guardian with relations:", error);
      return null;
    }
  }, [student.id]);

  const handleParentGuardianChange = useCallback(async (payload: PostgresChangePayload<any>) => {
    if (payload.eventType === "INSERT" || payload.eventType === "UPDATE") {
      const updatedParentGuardian = await fetchParentGuardianWithRelations(payload.new.parent_guardian_id);
      if (updatedParentGuardian) {
        setParentGuardians((prevParentGuardians) => {
          const guardianIndex = prevParentGuardians.findIndex((pg) => pg.id === updatedParentGuardian.id);
          if (guardianIndex !== -1) {
            const updatedGuardians = [...prevParentGuardians];
            updatedGuardians[guardianIndex] = updatedParentGuardian;
            return updatedGuardians;
          } else {
            return [...prevParentGuardians, updatedParentGuardian];
          }
        });
      }
    } else if (payload.eventType === "DELETE") {
      setParentGuardians((prevParentGuardians) =>
        prevParentGuardians.filter((pg) => pg.id !== payload.old.parent_guardian_id)
      );
    }
  }, [fetchParentGuardianWithRelations]);

  const handleAddParentGuardian = () => {
    openModal(
      <AddParentGuardianToStudentModal student={student} />,
      {}
    );
  };

  const handleRemoveParentGuardian = async (parentGuardianId: string) => {
    try {
      const { error } = await supabase
        .from("students_parent_guardians")
        .delete()
        .eq("student_id", student.id)
        .eq("parent_guardian_id", parentGuardianId);

      if (error) {
        throw error;
      }

      showToast("Parent/Guardian removed successfully.", "success", "solid");
      setParentGuardians((prev) => prev.filter((pg) => pg.id !== parentGuardianId));
    } catch (error) {
      console.error("Error removing parent/guardian:", error);
      showToast("Error removing parent/guardian.", "danger", "solid");
    }
  };

  useEffect(() => {
    fetchParentGuardians();

    const subscription = supabase
      .channel("public:students_parent_guardians")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "students_parent_guardians", filter: `student_id=eq.${student.id}` },
        async (payload) => {
          console.log("Change received!", payload);
          await handleParentGuardianChange(payload as any);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(subscription);
    };
  }, [student.id, fetchParentGuardians, handleParentGuardianChange]);

  return (
    <React.Fragment>
      <Button startDecorator={<AddIcon />} onClick={handleAddParentGuardian} sx={{ mb: 2 }}>
        Add Parent / Guardian
      </Button>
      <Sheet variant="outlined" sx={{ width: "100%", borderRadius: "sm", overflow: "auto", minHeight: 0 }}>
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="200px"
            sx={{ width: "100%" }}
          >
            <CircularProgress />
          </Box>
        ) : parentGuardians.length > 0 ? (
          <Table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Phone Number</th>
                <th>Email Address</th>
                <th>Relationship</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {parentGuardians.map((pg) => (
                <tr key={pg.id}>
                  <td>
                    <Typography>
                      {pg.first_name} {pg.last_name}
                    </Typography>
                  </td>
                  <td>
                    <Typography>{pg.phone_number}</Typography>
                  </td>
                  <td>
                    <Typography>{pg.email_address}</Typography>
                  </td>
                  <td>
                    <Typography>
                      {RelationshipTypeLabels[pg.relationship as RelationshipType]}
                    </Typography>
                  </td>
                  <td>
                    <Box display="flex" gap={1}>
                      <Tooltip title="Remove Parent / Guardian">
                        <IconButton onClick={() => handleRemoveParentGuardian(pg.id)}>
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="200px" sx={{ width: "100%" }}>
            <Typography level="body-lg" color="neutral">
              No Parents / Guardians
            </Typography>
          </Box>
        )}
      </Sheet>
    </React.Fragment>
  );
};

export default StudentParentGuardiansTable;
